import { useEffect } from 'react';
import { Navigate, NavigateProps, Outlet, useParams, useRoutes } from 'react-router-dom';
import { ApiExplorerController } from '../api/controllers/ApiExplorerController';
import { LoginController } from '../auth/controllers/LoginController';
import { SessionPickupController } from '../auth/controllers/SessionPickupController';
import { MainLayoutController } from '../common/controllers/MainLayoutController';
import { NotFoundController } from '../common/controllers/NotFoundController';
import { withAppConfig } from '../common/higher-order-components/with-app-config';
import { withAuthUser } from '../common/higher-order-components/with-auth-user';
import { UrlHelper } from '../common/utils/UrlHelper';
import { AccessibilityStatementController } from '../general-pages/controllers/AccessibilityStatementController';
import { UseConditionsStatementController } from '../general-pages/controllers/UseConditionsStatementController';
import { OrganizationCertificateController } from '../organization/certificates/controllers/OrganizationCertificateController';
import { OrganizationCertificatesController } from '../organization/certificates/controllers/OrganizationCertificatesController';
import { OrganizationDashboardController } from '../organization/dashboard/controllers/OrganizationDashboardController';
import { CrowdStrikeController } from '../organization/integrations/controllers/CrowdStrikeController';
import { IntegrationsController } from '../organization/integrations/controllers/IntegrationsController';
import { JiraController } from '../organization/integrations/controllers/JiraController';
import { JiraIssueCollectorController } from '../organization/integrations/controllers/JiraIssueCollectorController';
import { ServiceNowController } from '../organization/integrations/controllers/ServiceNowController';
import { TaniumController } from '../organization/integrations/controllers/TaniumController';
import { VenafiController } from '../organization/integrations/controllers/VenafiController';
import { OrganizationKeyController } from '../organization/keys/controllers/OrganizationKeyController';
import { OrganizationKeysController } from '../organization/keys/controllers/OrganizationKeysController';
import { CreateProfileController } from '../organization/profiles/controllers/CreateProfileController';
import { DuplicateProfileController } from '../organization/profiles/controllers/DuplicateProfileController';
import { OrganizationProfilesController } from '../organization/profiles/controllers/OrganizationProfilesController';
import { ProfileDetailsController } from '../organization/profiles/controllers/ProfileDetailsController';
import { OrganizationProjectsController } from '../organization/projects/controllers/OrganizationProjectsController';
import { CrowdStrikeScansList } from '../organization/scans/controllers/CrowdStrikeScansList';
import { ScansController } from '../organization/scans/controllers/ScansController';
import { OrganizationSensorsController } from '../organization/sensors/controllers/OrganizationSensorsController';
import { OrganizationUsersController } from '../organization/users/controllers/OrganizationUsersController';
import { UsersListController } from '../organization/users/controllers/UsersListController';
import { ProjectController } from '../projects/controllers/ProjectController';
import { ProjectDashboardController } from '../projects/dashboard/controllers/ProjectDashboardController';
import { ProjectExceptionsController } from '../projects/exceptions/controllers/ProjectExceptionsController';
import { ProjectIntegrationsController } from '../projects/integrations/controllers/ProjectIntegrationsController';
import { ProjectSlotsController } from '../projects/slots/controllers/ProjectSlotsController';
import { ReportCallSitesController } from '../reports/callsites/controllers/ReportCallSitesController';
import { ReportCertificateController } from '../reports/certificates/controllers/ReportCertificateController';
import { ReportCertificatesController } from '../reports/certificates/controllers/ReportCertificatesController';
import { ReportCiphersuitesController } from '../reports/ciphersuites/controllers/ReportCiphersuitesController';
import { ReportComparisonController } from '../reports/comparisons/controllers/ReportComparisonController';
import { ReportController } from '../reports/controllers/ReportController';
import { ReportHandshakesController } from '../reports/handshakes/controllers/ReportHandshakesController';
import { ReportSshHandshakesController } from '../reports/handshakes/controllers/ReportSshHandshakesController';
import { ReportTlsHandshakesController } from '../reports/handshakes/controllers/ReportTlsHandshakesController';
import { ReportIssuesController } from '../reports/issues/controllers/ReportIssuesController';
import { ReportIssueController } from '../reports/issues/issue/controllers/ReportIssueController';
import { InstanceController } from '../reports/issues/issue/instance/controllers/InstanceController';
import { ReportKeyController } from '../reports/keys/controllers/ReportKeyController';
import { ReportKeysController } from '../reports/keys/controllers/ReportKeysController';
import { OperationController } from '../reports/operations/controllers/OperationController';
import { ReportOperationsController } from '../reports/operations/controllers/ReportOperationsController';
import { ReportSummaryController } from '../reports/summary/controllers/ReportSummaryController';
import { LEGACY_PATHS, PATHS } from './routes';

/**
 * Wraps React's <Navigate /> component to map the path params of
 * the original route onto the redirected one.
 *
 */
const NavigateWithParams = ({ to, ...rest }: NavigateProps) => {
  const params = useParams();

  return <Navigate to={UrlHelper.generatePath(to as string, params)} {...rest} replace={true} />;
};

const NavigateToDocs = () => {
  useEffect(() => {
    //The `/docs` path renders public docs served by the Flask server.
    //Force a window navigation to correctly serve its static files.
    window.location.replace(UrlHelper.wrapKnowledgeBaseUrl(window.location.pathname));
  }, []);

  return null;
};

/**
 * Here we add all the routes that will be handled through SPA behaviour.
 * This router is here to support SPA behaviour and should be used primarily for that.
 *
 * Each route defined here represents the transferred functionality/page from the old app.
 *
 */
export const Router = (props: object) => {
  const MainReactComponent = withAuthUser(() => (
    <MainLayoutController>
      <Outlet />
    </MainLayoutController>
  ));

  const Routes = () => {
    return useRoutes([
      { path: PATHS.LOGIN_PAGE, element: <LoginController /> },
      { path: PATHS.SESSION_PICKUP_PAGE, element: <SessionPickupController /> },
      {
        path: PATHS.ROOT,
        /**
         * All Controllers requiring Authorization must either extend the BaseController or the MainLayoutController
         * to have authorization triggered. Authorization checks can be avoided overriding the `requiresAuthorization` method
         */
        element: <MainReactComponent />,
        children: [
          { path: '', element: <Navigate to={PATHS.ORGANIZATION_DASHBOARD} /> },
          { path: PATHS.ACCESSIBILITY_STATEMENT, element: <AccessibilityStatementController /> },
          { path: PATHS.USE_CONDITIONS_STATEMENT, element: <UseConditionsStatementController /> },
          { path: PATHS.ORGANIZATION_DASHBOARD, element: <OrganizationDashboardController /> },
          { path: PATHS.ORGANIZATION_PROJECTS, element: <OrganizationProjectsController /> },
          {
            path: PATHS.USERS,
            element: <OrganizationUsersController />,
            children: [{ path: '', element: <UsersListController /> }],
          },
          { path: PATHS.KEYS, element: <OrganizationKeysController /> },
          { path: PATHS.KEY, element: <OrganizationKeyController /> },
          { path: PATHS.PROFILES, element: <OrganizationProfilesController /> },
          { path: PATHS.PROFILE_DETAIL, element: <ProfileDetailsController /> },
          { path: PATHS.PROFILES_CREATE, element: <CreateProfileController /> },
          { path: PATHS.PROFILES_CREATE_TEMPLATE, element: <DuplicateProfileController /> },
          { path: PATHS.CERTIFICATES, element: <OrganizationCertificatesController /> },
          { path: PATHS.CERTIFICATE, element: <OrganizationCertificateController /> },
          { path: PATHS.SENSORS, element: <OrganizationSensorsController /> },
          {
            path: PATHS.SCANS,
            element: <ScansController />,
            children: [
              { path: '', element: <NavigateWithParams to={PATHS.SCANS_CROWDSTRIKE} /> },
              { path: PATHS.SCANS_CROWDSTRIKE, element: <CrowdStrikeScansList {...props} /> },
            ],
          },
          { path: PATHS.INTEGRATIONS, element: <IntegrationsController /> },
          { path: PATHS.INTEGRATION_JIRA, element: <JiraController /> },
          { path: PATHS.INTEGRATION_JIRA_FORM, element: <JiraIssueCollectorController /> },
          { path: PATHS.INTEGRATION_VENAFI, element: <VenafiController /> },
          { path: PATHS.INTEGRATION_CROWDSTRIKE, element: <CrowdStrikeController /> },
          { path: PATHS.INTEGRATION_TANIUM, element: <TaniumController /> },
          { path: PATHS.INTEGRATION_SERVICE_NOW, element: <ServiceNowController /> },
          {
            path: PATHS.PROJECT,
            element: <ProjectController />,
            children: [
              { path: '', element: <NavigateWithParams to={PATHS.PROJECT_DASHBOARD} /> },
              { path: PATHS.PROJECT_DASHBOARD, element: <ProjectDashboardController {...props} /> },
              { path: PATHS.PROJECT_SLOTS, element: <ProjectSlotsController {...props} /> },
              { path: PATHS.PROJECT_INTEGRATIONS, element: <ProjectIntegrationsController {...props} /> },
              { path: PATHS.PROJECT_FILTERS, element: <ProjectExceptionsController {...props} /> },
            ],
          },
          {
            path: PATHS.REPORT,
            element: <ReportController />,
            children: [
              {
                path: '',
                element: <ReportIssuesController {...props} />,
                children: [
                  {
                    path: PATHS.REPORT_ISSUE,
                    element: <ReportIssueController {...props} />,
                    children: [{ path: PATHS.REPORT_ISSUE_INSTANCE, element: <InstanceController {...props} /> }],
                  },
                ],
              },
              { path: PATHS.REPORT_CALL_SITES, element: <ReportCallSitesController {...props} /> },
              { path: PATHS.REPORT_INVENTORY, element: <ReportSummaryController {...props} /> },
              { path: PATHS.REPORT_KEY_LIFE_CYCLES, element: <ReportKeysController {...props} /> },
              { path: PATHS.REPORT_KEY_LIFE_CYCLE, element: <ReportKeyController {...props} /> },
              { path: PATHS.REPORT_CERTIFICATE, element: <ReportCertificateController {...props} /> },
              { path: PATHS.REPORT_CERTIFICATES, element: <ReportCertificatesController {...props} /> },
              {
                path: PATHS.REPORT_HANDSHAKES,
                element: <ReportHandshakesController {...props} />,
                children: [
                  { path: '', element: <NavigateWithParams to={PATHS.REPORT_TLS_HANDSHAKES} /> },
                  { path: PATHS.REPORT_TLS_HANDSHAKES, element: <ReportTlsHandshakesController {...props} /> },
                  { path: PATHS.REPORT_SSH_HANDSHAKES, element: <ReportSshHandshakesController {...props} /> },
                ],
              },
              { path: PATHS.REPORT_CIPHERSUITES, element: <ReportCiphersuitesController {...props} /> },
              { path: PATHS.REPORT_OPERATIONS, element: <ReportOperationsController {...props} /> },
              { path: PATHS.REPORT_OPERATION, element: <OperationController {...props} /> },
              { path: PATHS.REPORT_DIFF, element: <ReportComparisonController {...props} /> },
            ],
          },
          { path: PATHS.API_EXPLORER, element: <ApiExplorerController /> },

          // Redirections from legacy URLs
          { path: LEGACY_PATHS.ORGANIZATION_PROJECT, element: <NavigateWithParams to={PATHS.PROJECT} /> },
          { path: LEGACY_PATHS.PROJECT_NEW, element: <NavigateWithParams to={PATHS.ORGANIZATION_PROJECTS} /> },
          { path: LEGACY_PATHS.PROJECT_DELETE, element: <NavigateWithParams to={PATHS.ORGANIZATION_PROJECTS} /> },
          { path: LEGACY_PATHS.PROJECT_SLOTS_NEW, element: <NavigateWithParams to={PATHS.PROJECT_SLOTS} /> },
          { path: LEGACY_PATHS.PROJECT_SLOT, element: <NavigateWithParams to={PATHS.PROJECT_SLOTS} /> },
          { path: LEGACY_PATHS.PROJECT_SLOT_DELETE, element: <NavigateWithParams to={PATHS.PROJECT_SLOTS} /> },
          { path: LEGACY_PATHS.PROJECT_TRACES, element: <NavigateWithParams to={PATHS.PROJECT_SLOTS} /> },
          { path: LEGACY_PATHS.PROJECT_TRACE, element: <NavigateWithParams to={PATHS.PROJECT_SLOTS} /> },
          { path: LEGACY_PATHS.PROJECT_TRACE_EDIT, element: <NavigateWithParams to={PATHS.PROJECT_SLOTS} /> },
          { path: LEGACY_PATHS.PROJECT_TRACE_REMOVE, element: <NavigateWithParams to={PATHS.PROJECT_SLOTS} /> },
          { path: LEGACY_PATHS.PROJECT_REPORTS, element: <NavigateWithParams to={PATHS.PROJECT_SLOTS} /> },
          { path: LEGACY_PATHS.PROJECT_REPORT, element: <NavigateWithParams to={PATHS.REPORT} /> },
          { path: LEGACY_PATHS.PROJECT_REPORT_EDIT, element: <NavigateWithParams to={PATHS.REPORT} /> },
          { path: LEGACY_PATHS.PROJECT_REPORT_DIFFS, element: <NavigateWithParams to={PATHS.REPORT_DIFF} /> },
          { path: LEGACY_PATHS.PROJECT_FILTERS_CREATE, element: <NavigateWithParams to={PATHS.PROJECT_FILTERS} /> },
          { path: LEGACY_PATHS.PROJECT_FILTER_EDIT, element: <NavigateWithParams to={PATHS.PROJECT_FILTERS} /> },
          { path: LEGACY_PATHS.PROJECT_FILTER_REMOVE, element: <NavigateWithParams to={PATHS.PROJECT_FILTERS} /> },
          { path: LEGACY_PATHS.PROJECT_FILTER_TOGGLE, element: <NavigateWithParams to={PATHS.PROJECT_FILTERS} /> },
          { path: LEGACY_PATHS.PROJECT_TANIUM, element: <NavigateWithParams to={PATHS.PROJECT_INTEGRATIONS} /> },
          { path: LEGACY_PATHS.PROJECT_TANIUM_COLLECTOR, element: <NavigateWithParams to={PATHS.PROJECT_INTEGRATIONS} /> },
          { path: LEGACY_PATHS.PROJECT_TANIUM_STOP_COLLECTOR, element: <NavigateWithParams to={PATHS.PROJECT_INTEGRATIONS} /> },
          { path: LEGACY_PATHS.SLOT_REPORT, element: <NavigateWithParams to={PATHS.REPORT} /> },
          { path: LEGACY_PATHS.SLOT_REPORT_EDIT, element: <NavigateWithParams to={PATHS.REPORT} /> },
          { path: LEGACY_PATHS.SLOT_REPORT_DIFFS, element: <NavigateWithParams to={PATHS.REPORT_DIFF} /> },
          { path: LEGACY_PATHS.USER, element: <NavigateWithParams to={PATHS.USERS} /> },
          { path: LEGACY_PATHS.USERS_CREATE, element: <NavigateWithParams to={PATHS.USERS} /> },
          { path: LEGACY_PATHS.USER_REMOVE, element: <NavigateWithParams to={PATHS.USERS} /> },
          { path: LEGACY_PATHS.USER_RESET_PASSWORD, element: <NavigateWithParams to={PATHS.USERS} /> },
          { path: LEGACY_PATHS.CHANGE_PASSWORD, element: <NavigateWithParams to={PATHS.USERS} /> },
          { path: LEGACY_PATHS.PROFILE, element: <NavigateWithParams to={PATHS.PROFILE_DETAIL} /> },
          { path: LEGACY_PATHS.PROFILE_EDIT, element: <NavigateWithParams to={PATHS.PROFILES} /> },
          { path: LEGACY_PATHS.PROFILE_REMOVE, element: <NavigateWithParams to={PATHS.PROFILES} /> },
          { path: LEGACY_PATHS.API, element: <NavigateWithParams to={PATHS.API_EXPLORER} /> },
          { path: LEGACY_PATHS.API_RESET_KEY, element: <NavigateWithParams to={PATHS.API_EXPLORER} /> },
          { path: LEGACY_PATHS.REPORT_KEY_LIFE_CYCLE_OPERATION, element: <NavigateWithParams to={PATHS.REPORT_OPERATION} /> },
          { path: LEGACY_PATHS.REPORT_ISSUE_INSTANCE_OPERATION, element: <NavigateWithParams to={PATHS.REPORT_OPERATION} /> },
          { path: LEGACY_PATHS.REPORT_EXPORT, element: <NavigateWithParams to={PATHS.REPORT} /> },
          { path: LEGACY_PATHS.REPORT_EXPORT_PRINT, element: <NavigateWithParams to={PATHS.REPORT} /> },
          { path: LEGACY_PATHS.REPORT_EXPORT_JIRA, element: <NavigateWithParams to={PATHS.REPORT} /> },
          { path: LEGACY_PATHS.REPORT_EXPORT_JIRA_LIMIT, element: <NavigateWithParams to={PATHS.REPORT} /> },
          { path: LEGACY_PATHS.REPORT_EXPORT_JSON, element: <NavigateWithParams to={PATHS.REPORT} /> },
          { path: LEGACY_PATHS.REPORT_EXPORT_CSV_ALL, element: <NavigateWithParams to={PATHS.REPORT} /> },
          { path: LEGACY_PATHS.REPORT_EXPORT_CSV_SUMMARY, element: <NavigateWithParams to={PATHS.REPORT} /> },
          { path: LEGACY_PATHS.REPORT_EXPORT_CSV_OPERATIONS, element: <NavigateWithParams to={PATHS.REPORT} /> },
          { path: LEGACY_PATHS.REPORT_ISSUE_INSTANCE_JIRA, element: <NavigateWithParams to={PATHS.REPORT_ISSUE_INSTANCE} /> },
          { path: LEGACY_PATHS.REPORT_ISSUE_INSTANCE_EDIT, element: <NavigateWithParams to={PATHS.REPORT_ISSUE_INSTANCE} /> },
          { path: LEGACY_PATHS.LEGACY_REPORT_DIFF, element: <NavigateWithParams to={`${PATHS.REPORT_DIFF}?reportId=:comparedReportId`} /> },
          { path: LEGACY_PATHS.REPORT_REMOVE, element: <NavigateWithParams to={PATHS.REPORT} /> },
          { path: LEGACY_PATHS.REPORT_CALL_SITES_LEGACY, element: <NavigateWithParams to={PATHS.REPORT_CALL_SITES} /> },
          { path: LEGACY_PATHS.SETTINGS_JIRA, element: <NavigateWithParams to={PATHS.INTEGRATION_JIRA} /> },
          { path: LEGACY_PATHS.SETTINGS_JIRA_UNLINK, element: <NavigateWithParams to={PATHS.INTEGRATION_JIRA} /> },
          { path: LEGACY_PATHS.SETTINGS_JIRA_TOKEN, element: <NavigateWithParams to={PATHS.INTEGRATION_JIRA} /> },
          { path: LEGACY_PATHS.SETTINGS_JIRA_CALLBACK, element: <NavigateWithParams to={PATHS.INTEGRATION_JIRA} /> },
        ],
      },
      // Outdated KB links are redirected without going through `MainLayoutController`
      { path: `${LEGACY_PATHS.KNOWLEDGE_BASE}/*`, element: <NavigateToDocs /> },
      // All unhandled paths are considered "not found": either they never existed or they have been removed from the app
      { path: '*', element: <NotFoundController /> },
    ]);
  };

  const Wrapper = withAppConfig(Routes);
  return <Wrapper />;
};
